import { flatMap } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  AdditionalLoads,
  BasicProfile,
  BearingEnvelope,
  CeilingPlane,
  CeilingTray,
  ComponentDesign,
  FeaturePatternTrussContainer,
  isFeaturePatternTruss,
  JackGirderConnection,
  LayoutModel,
  LoadCase,
  ManualLoadSettings,
  MovingLoadSettings,
  ParapetManualLoadSettings,
  ProductionComponentInstance,
  RoofContainer,
  RoofPlane,
  SnappingUnderlay,
  TrussEnvelope,
  TrussGroup,
  WebbingInfo,
  WorkLine,
} from '@paragon/contract-library';
import { getLayoutModelsWithPendingChanges, LoadGroup } from '@paragon/layout-models';
import { getAllMembers } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, notNullish, ReadonlyRecord } from '@paragon/utilities';

import {
  getFirstSelectedItemIdentifierOrNull,
  getSelectedItemIdentifiers,
  getSingleSelectedItemIdentifierOrNull,
} from '../../library/selection/selectors';
import {
  createSelectedItemEntry,
  SelectableItem,
  SelectableItemEntry,
  SelectedItemIdentifier,
} from '../../library/selection/types';
import { RootState } from '..';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getLoadCasesForActiveComponentDesign } from './getLoadCasesForActiveComponentDesignSelector';
import { getProductionComponentInstanceByGuid } from './getProductionComponentInstanceByGuid';
import { getAllSnappingUnderlaysDictionary } from './layoutDataSelectors';
import { getInsertingOrRewebbingProfileOrNull } from './profileSelectors';
import { getActiveWebbingInfo } from './webbingInfoSelectors';

type AdditionalLoadType = 'manualLoad' | 'movingLoad' | 'parapetLoad';

export const ADDITIONAL_LOAD_PROPERTIES: { [type in AdditionalLoadType]: keyof AdditionalLoads } = {
  manualLoad: 'manualLoadSettings',
  movingLoad: 'movingLoadSettings',
  parapetLoad: 'parapetManualLoadSettings',
};

export type ComponentItemType =
  | 'analogLink'
  | 'panel'
  | 'analogJoint'
  | 'member'
  | 'brace'
  | 'gusset'
  | 'hanger'
  | 'platePair'
  | 'bearing'
  | 'splice'
  | 'referenceLine'
  | 'zone'
  | 'endCondition';

function findItem<T extends Exclude<SelectableItem, LoadGroup>>(list: readonly T[] | null | undefined, guid: string) {
  if (list == null) {
    console.error('Found null or undefined when looking up item that was expected to exist.');
    return null;
  }

  return list.find((item) => item.guid === guid);
}

export const findComponentDesignItemEntry = (
  type: ComponentItemType,
  guid: string,
  { component, analogModel }: ComponentDesign,
): SelectableItemEntry | null => {
  switch (type) {
    case 'analogJoint':
      return createSelectedItemEntry('analogJoint', findItem(analogModel?.analogJoints, guid));

    case 'analogLink':
      return createSelectedItemEntry('analogLink', findItem(analogModel?.analogLinks, guid));

    case 'panel':
      return createSelectedItemEntry('panel', findItem(analogModel?.panels, guid));

    case 'splice':
      return createSelectedItemEntry('splice', findItem(component.chordSplices, guid));

    case 'member':
    case 'brace':
      return createSelectedItemEntry(type, findItem(getAllMembers(component), guid));

    case 'gusset':
      return createSelectedItemEntry(type, findItem(component.gussets, guid));

    case 'hanger':
      return createSelectedItemEntry(type, findItem(component.hangers, guid));

    case 'bearing':
      return createSelectedItemEntry('bearing', findItem(component.bearings, guid));

    case 'platePair':
      return createSelectedItemEntry(
        'platePair',
        component.platePairs.find((pair) => pair.guid === guid),
      );

    case 'referenceLine':
      return createSelectedItemEntry('referenceLine', findItem(component.referenceLines, guid));

    case 'zone':
      if (!isFeaturePatternTruss(component)) return null;
      return createSelectedItemEntry('zone', findItem(component.zones, guid));

    case 'endCondition':
      if (!isFeaturePatternTruss(component)) return null;
      const endConditions = [component.leftEndCondition, component.rightEndCondition];
      return createSelectedItemEntry('endCondition', findItem(endConditions, guid));

    default:
      assertExhaustiveSwitch(type);
  }
};

function findZoneItemEntry(
  type: 'chase' | 'run',
  guid: string,
  { component }: ComponentDesign,
): SelectableItemEntry | null {
  if (!isFeaturePatternTruss(component)) return null;

  switch (type) {
    case 'run':
      const runs = flatMap(component.zones, (zone) => zone.runs);
      return createSelectedItemEntry('run', findItem(runs, guid));

    case 'chase':
      const chases = flatMap(component.zones, (zone) => zone.chases);
      return createSelectedItemEntry('chase', findItem(chases, guid));

    default:
      assertExhaustiveSwitch(type);
  }

  return null;
}

export type SelectedEntryFactory = (
  selectedItemIdentifier: SelectedItemIdentifier | null,
) => SelectableItemEntry | null;

const createSelectedEntryFactory =
  (
    activeComponentDesign: ComponentDesign | null,
    componentDesigns: ReadonlyRecord<string, ComponentDesign>,
    activeProfile: BasicProfile | null,
    layoutModels: Record<string, LayoutModel>,
    jackGirderConnections: Record<string, JackGirderConnection>,
    loadCases: readonly LoadCase[],
    roofPlanes: ReadonlyRecord<string, RoofPlane>,
    ceilingPlanes: ReadonlyRecord<string, CeilingPlane>,
    ceilingTrays: ReadonlyRecord<string, CeilingTray>,
    roofContainers: ReadonlyRecord<string, RoofContainer>,
    featurePatternContainers: ReadonlyRecord<string, FeaturePatternTrussContainer>,
    bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
    trussEnvelopes: ReadonlyRecord<string, TrussEnvelope>,
    trussGroups: { [trussGroupGuid: string]: TrussGroup },
    workLines: ReadonlyRecord<string, WorkLine>,
    snappingUnderlays: ReadonlyRecord<string, SnappingUnderlay>,
    productionComponentInstanceByGuid: { [productionComponentInstanceGuid: string]: ProductionComponentInstance },
    webbingInfo: WebbingInfo | null | undefined,
  ): SelectedEntryFactory =>
  (selectedItemIdentifier) => {
    if (selectedItemIdentifier == null) {
      return null;
    }

    switch (selectedItemIdentifier.type) {
      case 'chase':
      case 'run': {
        if (!activeComponentDesign) return null;
        return findZoneItemEntry(selectedItemIdentifier.type, selectedItemIdentifier.guid, activeComponentDesign);
      }

      case 'splice':
      case 'endCondition':
      case 'zone':
      case 'member':
      case 'brace':
      case 'gusset':
      case 'hanger':
      case 'bearing':
      case 'platePair':
      case 'analogJoint':
      case 'analogLink':
      case 'panel':
      case 'referenceLine': {
        if (!activeComponentDesign) return null;
        return findComponentDesignItemEntry(
          selectedItemIdentifier.type,
          selectedItemIdentifier.guid,
          activeComponentDesign,
        );
      }

      case 'componentDesign':
        return createSelectedItemEntry('componentDesign', componentDesigns[selectedItemIdentifier.guid]);

      case 'productionComponentInstance':
        return createSelectedItemEntry(
          'productionComponentInstance',
          productionComponentInstanceByGuid[selectedItemIdentifier.guid],
        );

      case 'project':
      case 'building':
      case 'level':
        return createSelectedItemEntry(selectedItemIdentifier.type, layoutModels[selectedItemIdentifier.guid]);

      case 'profileJoint': {
        return createSelectedItemEntry(
          'profileJoint',
          findItem(activeProfile?.profileJoints, selectedItemIdentifier.guid),
        );
      }

      case 'webJointSegment': {
        return createSelectedItemEntry(
          'webJointSegment',
          findItem(
            activeProfile?.profileLines.flatMap((profileLine) => profileLine.webJointSegments),
            selectedItemIdentifier.guid,
          ),
        );
      }

      case 'webJoint': {
        if (activeProfile) {
          const webJoint = activeProfile.profileLines
            .flatMap((line) => line.fixedJoints)
            .concat(
              activeProfile.profileLines.flatMap((line) =>
                line.webJointSegments.flatMap((webJointSegment) => webJointSegment.floatingJoints),
              ),
            )
            .find((joint) => joint.guid === selectedItemIdentifier.guid);

          return createSelectedItemEntry('webJoint', webJoint);
        }

        return null;
      }

      case 'webSegment': {
        if (webbingInfo) {
          const webSegment = webbingInfo.webbingRegions
            .flatMap((webbingRegion) => webbingRegion.webSegments)
            .find((webSegment) => webSegment.guid === selectedItemIdentifier.guid);

          return createSelectedItemEntry('webSegment', webSegment);
        }

        return null;
      }

      case 'loadCase': {
        return activeComponentDesign
          ? createSelectedItemEntry('loadCase', findItem(loadCases, selectedItemIdentifier.guid))
          : null;
      }

      case 'load': {
        if (!activeComponentDesign) return null;

        const load = loadCases
          .flatMap((loadCase) => loadCase.loads)
          .find((load) => load.guid === selectedItemIdentifier.guid);

        return createSelectedItemEntry('load', load);
      }

      case 'manualLoad':
      case 'movingLoad':
      case 'parapetLoad': {
        if (!activeComponentDesign) return null;

        const { additionalLoads } = activeComponentDesign;
        if (!additionalLoads) return null;

        // https://github.com/microsoft/TypeScript/issues/7294
        // Even though this cast is technically incorrect it's the closest we can get and still have some type safety.
        const relevantLoads = additionalLoads[ADDITIONAL_LOAD_PROPERTIES[selectedItemIdentifier.type]] as ReadonlyArray<
          ManualLoadSettings | MovingLoadSettings | ParapetManualLoadSettings
        >;

        const loadSettings = relevantLoads.find((load) => load.guid === selectedItemIdentifier.guid);
        return createSelectedItemEntry(selectedItemIdentifier.type, loadSettings) as SelectableItemEntry;
      }

      case 'jackGirderConnection':
        return createSelectedItemEntry('jackGirderConnection', jackGirderConnections[selectedItemIdentifier.guid]);

      case 'roofPlane': {
        const roofPlane = roofPlanes[selectedItemIdentifier.guid];
        if (roofPlane == null) return null;

        return createSelectedItemEntry('roofPlane', roofPlane);
      }

      case 'ceilingPlane': {
        const ceilingPlane = ceilingPlanes[selectedItemIdentifier.guid];
        if (ceilingPlane == null) return null;

        return createSelectedItemEntry('ceilingPlane', ceilingPlane);
      }

      case 'ceilingTray': {
        const ceilingTray = ceilingTrays[selectedItemIdentifier.guid];
        if (ceilingTray == null) return null;

        return createSelectedItemEntry('ceilingTray', ceilingTray);
      }

      case 'roofContainer': {
        const roofContainer = roofContainers[selectedItemIdentifier.guid];
        if (roofContainer == null) return null;

        return createSelectedItemEntry('roofContainer', roofContainer);
      }

      case 'featureArea': {
        for (const container of Object.values(featurePatternContainers)) {
          for (const featureArea of container.featureAreas) {
            if (featureArea.guid === selectedItemIdentifier.guid) {
              return createSelectedItemEntry('featureArea', featureArea);
            }
          }
        }

        return null;
      }

      case 'featurePatternContainer': {
        const featurePatternContainer = featurePatternContainers[selectedItemIdentifier.guid];
        if (featurePatternContainer == null) return null;

        return createSelectedItemEntry('featurePatternContainer', featurePatternContainer);
      }

      case 'trussEnvelope': {
        const trussEnvelope = trussEnvelopes[selectedItemIdentifier.guid];
        if (trussEnvelope == null) return null;

        return createSelectedItemEntry('trussEnvelope', trussEnvelope);
      }

      case 'trussGroup': {
        const trussGroup = trussGroups[selectedItemIdentifier.guid];
        if (trussGroup == null) return null;

        return createSelectedItemEntry('trussGroup', trussGroup);
      }

      case 'workLine': {
        const workLine = workLines[selectedItemIdentifier.guid];
        if (workLine == null) return null;

        return createSelectedItemEntry('workLine', workLine);
      }

      case 'snappingUnderlay': {
        const snappingUnderlay = snappingUnderlays[selectedItemIdentifier.guid];
        if (snappingUnderlay == null) return null;

        return createSelectedItemEntry('snappingUnderlay', snappingUnderlay);
      }

      case 'beam': {
        const bearingEnvelope = bearingEnvelopes[selectedItemIdentifier.guid];
        if (bearingEnvelope == null || bearingEnvelope.bearingType !== 'Beam') return null;

        return createSelectedItemEntry('beam', bearingEnvelope);
      }

      case 'wall': {
        const bearingEnvelope = bearingEnvelopes[selectedItemIdentifier.guid];
        if (bearingEnvelope == null || bearingEnvelope.bearingType !== 'Wall') return null;

        return createSelectedItemEntry('wall', bearingEnvelope);
      }

      default:
        assertExhaustiveSwitch(selectedItemIdentifier.type);
    }
  };

export const getSelectedEntryFactory = createSelector(
  getActiveComponentDesign,
  (state: RootState) => state.componentDesigns.data,
  getInsertingOrRewebbingProfileOrNull,
  getLayoutModelsWithPendingChanges,
  (state: RootState) => state.jackGirderConnections.data,
  getLoadCasesForActiveComponentDesign,
  (state: RootState) => state.layout.roofPlanes,
  (state: RootState) => state.layout.ceilingPlanes,
  (state: RootState) => state.layout.ceilingTrays,
  (state: RootState) => state.layout.roofContainers,
  (state: RootState) => state.layout.featurePatternContainers,
  (state: RootState) => state.layout.bearingEnvelopes,
  (state: RootState) => state.layout.trussEnvelopes,
  (state: RootState) => state.trussGroups,
  (state: RootState) => state.layout.workLines,
  getAllSnappingUnderlaysDictionary,
  getProductionComponentInstanceByGuid,
  getActiveWebbingInfo,

  createSelectedEntryFactory,
);

export const getFirstSelectedItemEntryOrNull = createSelector(
  getFirstSelectedItemIdentifierOrNull,
  getSelectedEntryFactory,

  (selectedItemIdentifier, selectedEntryFactory): SelectableItemEntry | null =>
    selectedItemIdentifier == null ? null : selectedEntryFactory(selectedItemIdentifier),
);

export const getSingleSelectedItemEntryOrNull = createSelector(
  getSingleSelectedItemIdentifierOrNull,
  getSelectedEntryFactory,

  (selectedItemIdentifier, selectedEntryFactory): SelectableItemEntry | null =>
    selectedItemIdentifier == null ? null : selectedEntryFactory(selectedItemIdentifier),
);

export const getSelectedItemEntries = createSelector(
  getSelectedItemIdentifiers,
  getSelectedEntryFactory,
  (identifiers, selectedEntryFactory) => identifiers.map(selectedEntryFactory).filter(notNullish),
);
